import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import './css/index.scss';
import PageTitle from '../../components/PageTitle';
import MyButton from '../../components/MyButton';
import MyLoading from '../../components/MyLoading';
import TablePagination from '../../components/TablePagination';
import Api from '../../Api';
import sendRequest from '../../helpers/sendRequest';
import {
  INTERFACE_LANGUAGES,
  ORIGINAL_LANGUAGE,
  TRANSLATION_LANGUAGE_CODES,
  getInterfaceLanguageLabel,
} from '../../helpers/interfaceLanguages';

const TABS = {
  directories: 'directories',
  stats: 'stats',
  uiStrings: 'uiStrings',
};

const FIELD_LABELS = {
  name: 'Название',
  inner_html: 'Содержимое (HTML)',
};

const PER_PAGE = 50;

/** Типы сущностей, временно скрытые из статистики и фильтра «Тип сущности». */
const HIDDEN_ENTITY_LABELS = ['Блоки информации', 'Пункты информации'];

function getVisibleEntityTypes(list = []) {
  return list.filter((item) => !HIDDEN_ENTITY_LABELS.includes(item.label));
}

/** Типы сущностей, для которых сервер отдаёт country_id / country_name. */
const ENTITY_TYPES_WITH_COUNTRY = ['article_blocks', 'articles'];

function coveragePercent(translated, total) {
  if (!total) return 0;
  return Math.round((translated / total) * 100);
}

function originalPreview(original = {}, fields = []) {
  if (original.name) return original.name;
  if (original.inner_html) {
    const plain = String(original.inner_html)
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return plain.slice(0, 120) || '—';
  }
  const parts = fields.map((field) => original[field]).filter(Boolean);
  return parts.join(' · ') || '—';
}

function countFilledLangs(translations = {}, fields = []) {
  return TRANSLATION_LANGUAGE_CODES.filter((code) => {
    const entry = translations[code] || {};
    return fields.some((field) => String(entry[field] || '').trim());
  }).length;
}

function buildDraftFromResponse(data) {
  const draft = {};
  TRANSLATION_LANGUAGE_CODES.forEach((code) => {
    draft[code] = {};
    (data.fields || []).forEach((field) => {
      draft[code][field] = data.translations?.[code]?.[field] ?? '';
    });
  });
  return draft;
}

function Translations() {
  const { profile } = useSelector((store) => store.account);
  const canEdit = Boolean(profile?.access_codes?.includes('editTranslations'));

  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(TABS.directories);

  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [uiStrings, setUiStrings] = useState(null);

  const [entityType, setEntityType] = useState(searchParams.get('entity_type') || '');
  const [search, setSearch] = useState('');
  // Фильтр по языку скрыт — не подхватываем его из URL, чтобы он не действовал незаметно
  // const [lang, setLang] = useState(searchParams.get('lang') || '');
  const [lang, setLang] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(searchParams.get('only_missing') === 'true');
  const [page, setPage] = useState(1);

  const [listLoading, setListLoading] = useState(false);
  const [listData, setListData] = useState(null);
  const [metaLoading, setMetaLoading] = useState(true);

  const [selectedId, setSelectedId] = useState(searchParams.get('entity_id') || '');
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorData, setEditorData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const entityTypes = useMemo(() => getVisibleEntityTypes(meta?.entity_types), [meta]);
  const withCountry = ENTITY_TYPES_WITH_COUNTRY.includes(entityType);

  const selectedEntityMeta = useMemo(
    () => entityTypes.find((item) => item.entity_type === entityType) || null,
    [entityTypes, entityType],
  );

  const loadMetaAndStats = useCallback(async () => {
    setMetaLoading(true);
    const [metaRes, statsRes] = await Promise.all([
      sendRequest({ request: Api.getTranslationsMeta, warnErrorText: 'translations meta' }),
      sendRequest({ request: Api.getTranslationsStats, warnErrorText: 'translations stats' }),
    ]);
    if (metaRes?.status === 'ok') {
      setMeta(metaRes);
      setEntityType((prev) => prev || getVisibleEntityTypes(metaRes.entity_types)[0]?.entity_type || '');
    }
    if (statsRes?.status === 'ok') {
      setStats(statsRes);
    }
    setMetaLoading(false);
  }, []);

  const loadList = useCallback(async () => {
    if (!entityType) return;
    const params = {
      entityType,
      page,
      per_page: PER_PAGE,
    };
    if (search.trim()) params.search = search.trim();
    if (lang) params.lang = lang;
    if (onlyMissing) params.only_missing = true;

    const data = await sendRequest({
      request: Api.getTranslationsList,
      payload: params,
      warnErrorText: 'translations list',
      setLoading: setListLoading,
    });
    if (data?.status === 'ok') {
      setListData(data);
    } else {
      setListData(null);
    }
  }, [entityType, page, search, lang, onlyMissing]);

  const openEditor = useCallback(async (id) => {
    if (!entityType || !id) return;
    setSelectedId(id);
    setSaveMessage('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('entity_type', entityType);
      next.set('entity_id', id);
      return next;
    });

    const data = await sendRequest({
      request: Api.getTranslation,
      payload: { entityType, entityId: id },
      warnErrorText: 'translation card',
      setLoading: setEditorLoading,
    });

    if (data?.status === 'ok') {
      setEditorData(data);
      setDraft(buildDraftFromResponse(data));
    } else {
      setEditorData(null);
      setDraft(null);
    }
  }, [entityType, setSearchParams]);

  const closeEditor = () => {
    setSelectedId('');
    setEditorData(null);
    setDraft(null);
    setSaveMessage('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('entity_id');
      return next;
    });
  };

  const onDraftChange = (code, field, value) => {
    setDraft((prev) => ({
      ...prev,
      [code]: {
        ...(prev?.[code] || {}),
        [field]: value,
      },
    }));
  };

  const onSave = async () => {
    if (!canEdit || !draft || !selectedId) return;
    setSaveMessage('');

    const data = await sendRequest({
      request: Api.putTranslations,
      payload: {
        entityType,
        entityId: selectedId,
        translations: draft,
      },
      warnErrorText: 'save translations',
      setLoading: setSaveLoading,
    });

    if (data?.status === 'ok') {
      setEditorData((prev) => (prev ? { ...prev, translations: data.translations } : prev));
      setDraft(buildDraftFromResponse({
        fields: editorData?.fields || [],
        translations: data.translations,
      }));
      setSaveMessage('Сохранено');
      loadList();
      loadMetaAndStats();
    } else {
      setSaveMessage(data?.message || 'Не удалось сохранить');
    }
  };

  const onDeleteAll = async () => {
    if (!canEdit || !selectedId) return;
    if (!window.confirm('Удалить все переводы этой записи?')) return;

    const data = await sendRequest({
      request: Api.deleteTranslations,
      payload: { entityType, entityId: selectedId },
      warnErrorText: 'delete translations',
      setLoading: setSaveLoading,
    });

    if (data?.status === 'ok') {
      openEditor(selectedId);
      loadList();
      loadMetaAndStats();
    } else {
      setSaveMessage(data?.message || 'Не удалось удалить');
    }
  };

  const loadUiStrings = async () => {
    const data = await sendRequest({
      request: Api.getTranslationsUiStrings,
      warnErrorText: 'ui strings',
      setLoading: setListLoading,
    });
    if (data?.status === 'ok') {
      setUiStrings(data);
    }
  };

  useEffect(() => {
    loadMetaAndStats();
  }, [loadMetaAndStats]);

  useEffect(() => {
    if (tab === TABS.directories && entityType && !selectedId) {
      loadList();
    }
  }, [tab, entityType, page, search, lang, onlyMissing, selectedId, loadList]);

  useEffect(() => {
    const initialId = searchParams.get('entity_id');
    const initialType = searchParams.get('entity_type');
    if (initialType && initialId && entityType === initialType && !editorData) {
      openEditor(initialId);
    }
  }, [entityType]);

  useEffect(() => {
    if (tab === TABS.uiStrings && !uiStrings) {
      loadUiStrings();
    }
  }, [tab]);

  const pagesCount = Math.max(1, Math.ceil((listData?.total || 0) / PER_PAGE));

  const coverageByEntity = useMemo(() => {
    if (!stats?.coverage || !stats?.totals) return [];
    return entityTypes.map((entity) => {
      const total = stats.totals[entity.entity_type] || 0;
      const byLang = TRANSLATION_LANGUAGE_CODES.map((code) => {
        const rows = stats.coverage.filter(
          (row) => row.entity_type === entity.entity_type && row.lang === code,
        );
        const translated = rows.length
          ? Math.min(...rows.map((row) => row.translated_count || 0))
          : 0;
        return { code, translated, percent: coveragePercent(translated, total) };
      });
      const avg = byLang.length
        ? Math.round(byLang.reduce((sum, item) => sum + item.percent, 0) / byLang.length)
        : 0;
      return {
        entity_type: entity.entity_type,
        label: entity.label,
        total,
        byLang,
        avg,
      };
    });
  }, [stats, entityTypes]);

  const uiStringEntries = useMemo(() => {
    if (!uiStrings) return [];
    if (Array.isArray(uiStrings.items)) return uiStrings.items;
    if (uiStrings.strings && typeof uiStrings.strings === 'object') {
      return Object.entries(uiStrings.strings).map(([key, value]) => ({ key, translations: value }));
    }
    if (typeof uiStrings === 'object') {
      return Object.entries(uiStrings)
        .filter(([key]) => !['status', 'message'].includes(key))
        .map(([key, value]) => ({ key, translations: value }));
    }
    return [];
  }, [uiStrings]);

  if (metaLoading) {
    return (
      <div className="translations">
        <PageTitle text="Переводы" />
        <MyLoading />
      </div>
    );
  }

  return (
    <div className="translations">
      <PageTitle text="Переводы справочников и статей" />

      <div className="translations_tabs">
        <button
          type="button"
          className={`translations_tab ${tab === TABS.directories ? 'active' : ''}`}
          onClick={() => { setTab(TABS.directories); closeEditor(); }}
        >
          Справочники
        </button>
        <button
          type="button"
          className={`translations_tab ${tab === TABS.stats ? 'active' : ''}`}
          onClick={() => setTab(TABS.stats)}
        >
          Статистика
        </button>
        {/* Раздел «UI-строки сервера» временно скрыт
        <button
          type="button"
          className={`translations_tab ${tab === TABS.uiStrings ? 'active' : ''}`}
          onClick={() => setTab(TABS.uiStrings)}
        >
          UI-строки сервера
        </button>
        */}
      </div>

      {tab === TABS.stats && (
        <div className="translations_stats">
          {coverageByEntity.map((item) => (
            <div key={item.entity_type} className="translations_stat_card">
              <div className="translations_stat_card_title">
                {item.label}
                {' '}
                (
                {item.total}
                )
              </div>
              {item.byLang.map((langItem) => (
                <div key={langItem.code} className="translations_stat_card_row">
                  <span>{getInterfaceLanguageLabel(langItem.code)}</span>
                  <span>
                    {langItem.translated}
                    /
                    {item.total}
                    {' '}
                    (
                    {langItem.percent}
                    %)
                  </span>
                </div>
              ))}
              <div className="translations_stat_card_bar">
                <span style={{ width: `${item.avg}%` }} />
              </div>
            </div>
          ))}
          {!coverageByEntity.length && (
            <p className="translations_empty">Нет данных статистики</p>
          )}
        </div>
      )}

      {/* Раздел «UI-строки сервера» временно скрыт
      {tab === TABS.uiStrings && (
        <div>
          <p className="translations_hint">
            Словарь статичных серверных текстов (пуши, письма). Только просмотр — строки лежат в коде сервера.
          </p>
          {listLoading && <MyLoading />}
          {!listLoading && uiStringEntries.map((item) => (
            <div key={item.key} className="translations_ui_block">
              <div className="translations_ui_block_key">{item.key}</div>
              {INTERFACE_LANGUAGES.map((language) => (
                <div key={language.code} className="translations_ui_block_row">
                  <span>{language.code}</span>
                  <span>
                    {typeof item.translations === 'object'
                      ? (item.translations?.[language.code] ?? '—')
                      : String(item.translations ?? '—')}
                  </span>
                </div>
              ))}
            </div>
          ))}
          {!listLoading && !uiStringEntries.length && (
            <p className="translations_empty">UI-строки не найдены</p>
          )}
        </div>
      )}
      */}

      {tab === TABS.directories && !selectedId && (
        <div>
          <div className="translations_toolbar">
            <div className="translations_field">
              <label htmlFor="tr_entity">Тип сущности</label>
              <select
                id="tr_entity"
                value={entityType}
                onChange={(e) => {
                  setEntityType(e.target.value);
                  setPage(1);
                  setSearchParams({ entity_type: e.target.value });
                }}
              >
                {entityTypes.map((item) => (
                  <option key={item.entity_type} value={item.entity_type}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="translations_field">
              <label htmlFor="tr_search">Поиск по оригиналу</label>
              <input
                id="tr_search"
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Название…"
              />
            </div>
            {/* Фильтр «Язык (для «без перевода»)» временно скрыт
            <div className="translations_field">
              <label htmlFor="tr_lang">Язык (для «без перевода»)</label>
              <select
                id="tr_lang"
                value={lang}
                onChange={(e) => {
                  setLang(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">Все</option>
                {TRANSLATION_LANGUAGE_CODES.map((code) => (
                  <option key={code} value={code}>
                    {getInterfaceLanguageLabel(code)}
                  </option>
                ))}
              </select>
            </div>
            */}
            <label className="translations_checkbox">
              <input
                type="checkbox"
                checked={onlyMissing}
                onChange={(e) => {
                  setOnlyMissing(e.target.checked);
                  setPage(1);
                }}
              />
              Только без перевода
            </label>
          </div>

          {listLoading && <MyLoading />}

          {!listLoading && listData?.items?.length ? (
            <div>
              <div className="translations_table">
                <div className="translations_table_head">
                  <span>Оригинал (ru)</span>
                  <span>Переводы</span>
                  <span>Покрытие</span>
                  <span />
                </div>
                {listData.items.map((item) => {
                  const fields = listData.fields || selectedEntityMeta?.fields || ['name'];
                  const filled = countFilledLangs(item.translations, fields);
                  return (
                    <div
                      key={item.entity_id}
                      className="translations_table_row"
                      onClick={() => openEditor(item.entity_id)}
                    >
                      <span>
                        {originalPreview(item.original, fields)}
                        {withCountry && item.country_name ? (
                          <span className="translations_table_country">{item.country_name}</span>
                        ) : null}
                      </span>
                      <div className="translations_langs_preview">
                        {TRANSLATION_LANGUAGE_CODES.map((code) => {
                          const entry = item.translations?.[code] || {};
                          const has = fields.some((field) => String(entry[field] || '').trim());
                          return (
                            <span
                              key={code}
                              className={`translations_lang_chip ${has ? 'filled' : ''}`}
                            >
                              {code}
                            </span>
                          );
                        })}
                      </div>
                      <span>
                        {filled}
                        /
                        {TRANSLATION_LANGUAGE_CODES.length}
                      </span>
                      <MyButton
                        text="Открыть"
                        secondary
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditor(item.entity_id);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="translations_pagination">
                <TablePagination
                  page={page}
                  pagesCount={pagesCount}
                  setPage={setPage}
                />
              </div>
            </div>
          ) : null}

          {!listLoading && listData && !listData.items?.length && (
            <p className="translations_empty">Записей не найдено</p>
          )}
        </div>
      )}

      {tab === TABS.directories && selectedId && (
        <div className="translations_editor">
          {editorLoading && <MyLoading />}
          {!editorLoading && editorData && draft && (
            <div>
              <div className="translations_editor_header">
                <div>
                  <div className="translations_editor_title">
                    {selectedEntityMeta?.label || entityType}
                  </div>
                  {withCountry && editorData.country_name ? (
                    <div className="translations_editor_subtitle">
                      Страна:
                      {' '}
                      {editorData.country_name}
                    </div>
                  ) : null}
                  <div className="translations_editor_subtitle">
                    ID:
                    {' '}
                    {editorData.entity_id}
                  </div>
                </div>
                <div className="translations_editor_actions">
                  <MyButton text="Назад к списку" secondary onClick={closeEditor} />
                  {canEdit && (
                    <MyButton text="Удалить все" secondary onClick={onDeleteAll} />
                  )}
                  {canEdit && (
                    <MyButton text="Сохранить" onClick={onSave} loading={saveLoading} />
                  )}
                </div>
              </div>

              {!canEdit && (
                <div className="translations_readonly_note">
                  Просмотр. Для редактирования нужен доступ «Редактировать переводы справочников и статей».
                </div>
              )}

              <p className="translations_hint">
                Оригинал (
                {ORIGINAL_LANGUAGE}
                ) правится в обычной карточке сущности. Пустая строка удаляет перевод поля.
              </p>

              <div className="translations_original">
                <div className="translations_original_label">Оригинал (русский)</div>
                {(editorData.fields || []).map((field) => (
                  <div key={field} className="translations_original_field">
                    <strong>{FIELD_LABELS[field] || field}</strong>
                    {field === 'inner_html' ? (
                      <pre>{editorData.original?.[field] || '—'}</pre>
                    ) : (
                      <p>{editorData.original?.[field] || '—'}</p>
                    )}
                  </div>
                ))}
              </div>

              {TRANSLATION_LANGUAGE_CODES.map((code) => (
                <div key={code} className="translations_lang_block">
                  <div className="translations_lang_block_title">
                    <span>
                      {getInterfaceLanguageLabel(code)}
                      {' '}
                      (
                      {code}
                      )
                    </span>
                  </div>
                  {(editorData.fields || []).map((field) => (
                    <div key={`${code}-${field}`}>
                      <span className="translations_field_label">{FIELD_LABELS[field] || field}</span>
                      {field === 'inner_html' ? (
                        <textarea
                          className="translations_textarea"
                          value={draft[code]?.[field] || ''}
                          onChange={(e) => onDraftChange(code, field, e.target.value)}
                          disabled={!canEdit}
                        />
                      ) : (
                        <input
                          className="translations_input"
                          type="text"
                          value={draft[code]?.[field] || ''}
                          onChange={(e) => onDraftChange(code, field, e.target.value)}
                          disabled={!canEdit}
                        />
                      )}
                    </div>
                  ))}
                </div>
              ))}

              {saveMessage ? <p className="translations_info">{saveMessage}</p> : null}
            </div>
          )}
          {!editorLoading && !editorData && (
            <p className="translations_empty">Не удалось загрузить карточку перевода</p>
          )}
        </div>
      )}
    </div>
  );
}

export default Translations;
